<template>
    <div class="user_docs">
        <div class="navbar">
            <div class="navbar_header">
                <h1>Docs</h1>
            </div>
            <div class="options_group">
                <button v-for="pageName in Object.keys(pageContent)" :key="pageName" @click="selectPage(pageName)" :class="{ selected: selectedPage === pageName }">
                    {{ pageName }}
                </button>
            </div>
            <div class="navbar_footer">
                <a href="#top" class="top_link" @click.prevent="onDocClick">Back to top</a>
            </div>
        </div>
        <div class="doc_content" ref="docContentRef">
            <article v-html="currentPageHtml" class="doc-article" @click="onDocClick" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";

import gettingStartedHtml from "../docs/html/getting_started.html?raw";
import vaAiExplainedHtml from "../docs/html/virtual_acoustician_ai_explained.html?raw";
import annotatorsFieldGuideHtml from "../docs/html/annotators_field_guide.html?raw";
import frequentlyAskedQuestionsHtml from "../docs/html/frequently_asked_questions.html?raw";

// Build-time resolved URLs for all doc images
const docImages = import.meta.glob("../docs/assets/img/*", {
  eager: true,
  import: "default",
}) as Record<string, string>;

// Rewrite image src paths once, when module is initialised
function withResolvedDocImagePaths(html: string): string {
  return html.replace(/src="\.\.\/assets\/img\/([^"]+)"/g, (_match, fileName: string) => {
    const key = `../docs/assets/img/${fileName}`;
    const resolved = docImages[key];
    return resolved ? `src="${resolved}"` : `src="../assets/img/${fileName}"`;
  });
}

// Our page content is static and imported at build time so we can resolve image paths once here.
const pageContent: Record<string, string> = {
  "Getting Started": withResolvedDocImagePaths(gettingStartedHtml),
  "Virtual Acoustician AI Explained": withResolvedDocImagePaths(vaAiExplainedHtml),
  "Annotator's Field Guide": withResolvedDocImagePaths(annotatorsFieldGuideHtml),
  "Frequently Asked Questions": withResolvedDocImagePaths(frequentlyAskedQuestionsHtml),
};

// State for the selected page and the scrolling container ref (from the contents)
const selectedPage = ref<string>("Getting Started");
const docContentRef = ref<HTMLElement | null>(null);

function selectPage(pageName: string) {
  selectedPage.value = pageName;
}

const currentPageHtml = computed(() => {
  return pageContent[selectedPage.value] ?? "<h1>Not found</h1>";
});

// On selecting a href link in the (in the doc contents), smoothly scrolls to associted heading (by href id)
function onDocClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  const link = target.closest('a[href^="#"]') as HTMLAnchorElement | null;
  if (!link) return;

  const href = link.getAttribute("href");
  if (!href || href === "#") return;

  event.preventDefault();

  const id = decodeURIComponent(href.slice(1));
  const container = docContentRef.value;
  if (!container) return;

  const escapedId =
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
      ? CSS.escape(id)
      : id.replace(/["\\]/g, "\\$&");

  const section = container.querySelector(`#${escapedId}`) as HTMLElement | null;
  if (!section) return;

  section.scrollIntoView({ behavior: "smooth", block: "start" });
  history.replaceState(null, "", `#${encodeURIComponent(id)}`);
}

watch(selectedPage, async () => {
  await nextTick();
  docContentRef.value?.scrollTo({ top: 0, behavior: "auto" });
});
</script>

<style scoped>
.user_docs {
  display: flex;
  align-items: stretch; /* stretch children to full container height */
  width: 100%;
  height: calc(100vh - var(--header-height));
  overflow: hidden;
  box-sizing: border-box;
}

.navbar {
  display: flex;          /* required for margin-top:auto footer */
  flex-direction: column;
  flex: 0 0 15%;
  height: 100%;
  min-height: 100%;
  border-right: 1px solid var(--colour-divider-dark-2);
  box-sizing: border-box;
}

.navbar_header {
  flex: 0 0 auto;
}

.options_group {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  /* optional if many buttons */
  overflow-y: auto;
}

.options_group button {
  width: 100%;
  text-align: left;
  border-radius: 0px;
  border: 1px solid var(--color-primary-muted);
  background-color: var(--color-primary);
  color: var(--colour-bg-subtle);
}
.options_group button:hover {
  background-color: var(--color-primary-hover);
}
.options_group button.selected {
  background-color: var(--color-primary-muted);
}

.navbar_footer {
margin-top: auto;
padding: var(--spacing-md);
}

.top_link {
display: block;
text-align: center;
border: 1px solid var(--color-primary-muted);
background: var(--color-primary-muted);
color: var(--colour-bg-subtle);
padding: var(--spacing-sm) var(--spacing-md);
text-decoration: none;
}

.doc_content {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  overflow-x: auto; /* important for wide content */
  padding-left: var(--spacing-md);
  padding-right: var(--spacing-lg);
  box-sizing: border-box;
}

.doc_content :deep(.doc-page) {
  width: 100%;
  margin: 0;
  text-align: left;
}

.doc_content :deep(img) {
  max-width: 100%;
}

.doc_content :deep(figcaption) {
  color: var(--colour-text-body-muted);
}

.doc_content :deep(table) {
  width: 100%;
  table-layout: auto;
  border-collapse: collapse;
  background: var(--colour-surface);
}

.doc_content :deep(th),
.doc_content :deep(td) {
  border: 1px solid var(--colour-divider-dark-2);
  padding: var(--spacing-sm) var(--spacing-md);
  text-align: left;
  vertical-align: top;
  white-space: normal;
  overflow-wrap: normal;
  word-break: normal;
}

.doc_content :deep(thead th) {
  background: var(--color-primary-muted);
  color: var(--colour-bg-subtle);
  font-weight: bold;
}

.doc_content :deep(b) {
  font-weight: bold;
  color: var(--color-primary-muted);
}

.doc_content :deep(h3) {
  color: var(--color-primary-muted);
}

.doc_content :deep(.stepgroup){
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  text-align: center;
}

.doc_content :deep(.stepgroup > div){
  flex: 1 1 0;
  padding: var(--spacing-md) var(--spacing-lg) var(--spacing-sm);
}

.doc_content :deep(.step_odd) {
  background-color: var(--color-primary);
}

.doc_content :deep(.step_even){
  background-color: var(--color-secondary);
}

.doc_content :deep(.reference_card) {
  display: flex;
  flex-direction: row;
  border: 1px solid var(--colour-divider-dark-2);
  overflow: hidden;
  margin: var(--spacing-lg) 0;
}

.doc_content :deep(.reference_header) {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border-right: 1px solid var(--colour-divider-dark-2);
  flex: 0 0 auto;
  width: 12%;
  align-items: center;
  background-color: var(--color-primary-muted);
  color: var(--colour-bg-subtle);
}

.doc_content :deep(.reference_header h4) {
  font-weight: bold;
}

.doc_content :deep(.reference_header p) {
  font-size: var(--font-size-sm);
}

.doc_content :deep(.reference_card_body) {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  flex: 1 1 auto;
}

.doc_content :deep(.reference_card_body h5) {
  margin: 0;
  color: var(--color-primary-muted);
  font-size: 1em;
  font-weight: bold;
}

.doc_content :deep(.reference_card_body p) {
  margin: 0;
}

.doc_content :deep(.reference_card_body ul) {
  margin: 0;
  padding-left: var(--spacing-lg);
}

.doc_content :deep(.reference_card_body li) {
  margin: var(--spacing-sm) 0;
}

.doc_content :deep(.mistake_card) {
  display: flex;
  flex-direction: row;
  border: 1px solid var(--colour-divider-dark-2);
  overflow: hidden;
  margin: var(--spacing-lg) 0;
}

.doc_content :deep(.mistake_card_header) {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 12%;
  min-width: 0;
  padding: var(--spacing-md) var(--spacing-lg);
  border-right: 1px solid var(--colour-divider-dark-2);
  background-color: var(--warning-color-muted);
  color: var(--colour-bg-subtle);
  flex: 0 0 auto;
}

.doc_content :deep(.mistake_card_header h4) {
  margin: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.doc_content :deep(.mistake_card_body) {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  flex: 1 1 auto;
  background: var(--colour-surface-subtle);
}

.doc_content :deep(.mistake_card_body p) {
  margin: 0;
}

.doc_content :deep(.mistake_card_body em) {
  color: var(--warning-color-muted);
  font-style: normal;
  font-weight: bold;
}
.doc_content :deep(.mistake_card_body b) {
  color: var(--color-primary-muted);
}

.doc_content :deep(.comparison_card){
  display: flex;
  flex-direction: row;
}
.doc_content :deep(.comparison_card h4) {
  font-weight: bold;
}
.doc_content :deep(.comparison_card > div){
  flex: 1 1 0;
  padding: var(--spacing-md) var(--spacing-lg) var(--spacing-sm);
}
.doc_content :deep(.comparison_card_first) {
  border: 1px solid var(--colour-divider-dark-2);
  background-color: var(--color-primary);
  color: var(--colour-bg-subtle);
}
.doc_content :deep(.comparison_card_second) {
  border: 1px solid var(--colour-divider-dark-2);
}
.doc_content :deep(p),
.doc_content :deep(li),
.doc_content :deep(h1),
.doc_content :deep(h2),
.doc_content :deep(h3),
.doc_content :deep(figcaption),
.doc_content :deep(blockquote) {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
